import { z } from "zod";

// Enum for form steps in listing wizard
export enum FormStep {
  PropertyInformation = 0,
  PropertyDetails = 1,
  Location = 2,
  Media = 3,
  Contact = 4,
  TitleDescription = 5,
  Review = 6,
}

// Basic listing form schema
export const listingFormSchema = z.object({
  id: z.number().optional(), // Added for existing listings
  // Step 0: Basic Information
  propertyType: z.number().min(1, "Property type is required"), // Changed to store ID
  propertyTypeDisplayName: z.string().optional(), // Can be removed or kept for initial display if needed elsewhere
  listingType: z.string().min(1, "Listing type is required"),
  price: z
    .number({ required_error: "Price is required" })
    .gt(0, "Price must be greater than 0"),
  currency: z.string().default("LKR"),
  pricePeriod: z.string().min(1, "Price period is required"),

  // Step 1: Property Details
  size: z.number().optional(),
  size_unit: z.string().optional().default("sqft"),
  floorSizeSqFt: z.number().nullable().optional(),
  plotSizeSqft: z.number().nullable().optional(),
  rooms: z.number().nullable().optional(),
  bedrooms: z.number().nullable().optional(),
  bathrooms: z.number().nullable().optional(),
  parkings: z.number().nullable().optional(),
  floors: z.number().nullable().optional(),
  furnishing: z.string().optional(),
  yearBuilt: z.number().nullable().optional(), // Added yearBuilt
  status: z.string().optional(), // This is property condition status
  advancePayment: z.number().nullable().optional(),
  minimumTermValue: z.number().nullable().optional(),
  minimumTermUnit: z.enum(["months", "years"]).optional().default("months"),
  depositRequired: z.number().nullable().optional(),
  shortTermAvailable: z.boolean().optional().default(false),
  billsIncluded: z.boolean().optional().default(false),
  attributes: z.array(z.number()).default([]), // Unified attributes

  // Step 2: Location
  location: z.object({
    provinceId: z.number().optional(), // New field for province ID
    districtId: z.number().optional(), // New field for district ID
    // The 'province', 'district', 'city' string fields can be kept as optional if used for temporary display before conversion to ID.
    // For ReviewStep, we will rely on the IDs.
    province: z.string().optional(),
    district: z.string().optional(),
    city: z.string().optional(),
    city_id: z.number().min(1, "City is required"), // Already correctly storing city ID
    address: z.string().optional().or(z.literal("")),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
  }),

  // Step 3: Media (handled separately)
  images: z
    .array(
      z.object({
        id: z.union([z.string(), z.number()]),
        url: z.string(),
        is_primary: z.boolean().optional(), // Changed to is_primary to match backend
      })
    )
    .min(3, "At least 3 images are required")
    .max(6, "Maximum of 6 images allowed")
    .refine((images) => images.some((img) => img.is_primary), {
      message: "One image must be set as primary",
    }),
  videoUrl: z.string().url("Invalid URL format").optional().nullable(), // Added videoUrl

  // Step 4: Contact Details
  contactDetails: z.object({
    name: z.string().min(1, "Name/Company Name is required").default(""),
    phone: z.string().min(1, "Contact Number is required").default(""),
    email: z
      .string()
      .email("Invalid email address")
      .min(1, "Email Address is required")
      .default(""),
    whatsapp: z.boolean().optional().default(false),
    botim: z.boolean().optional().default(false),
    hideContactUntilRequest: z.boolean().optional().default(false),
  }),

  // Step 5: Title and Description
  title: z.string().min(5, "Title must be at least 5 characters"),
  description: z.string().min(20, "Description must be at least 20 characters"),
  cplk_ref: z.string().nullable().optional(), // Add cplk_ref

  // Step 6: Review (Terms and Conditions)
  authorizedToSell: z.boolean().refine((val) => val === true, {
    message: "You must confirm authorization to sell/rent this property.",
  }),
  termsAccepted: z.boolean().refine((val) => val === true, {
    message: "You must accept the Terms and Conditions.",
  }),
});

export type ListingFormValues = z.infer<typeof listingFormSchema>;
